import { Box, Paper, Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ScheduleCallsPerson } from "../receptionKpisRows";

type ScheduleCallsBarChartProps = {
    persons: Array<ScheduleCallsPerson>;
    points: Array<Record<string, string | number>>;
    height?: number;
};

const formatHours = (hours: number) => `${hours.toFixed(2)}h`;
const labelForHours = (value: string | number) => {
    const hours = Number(value);
    const label = hours > 0 ? hours.toFixed(1) : "";
    return label;
};

// Réplica nativa do gráfico do Zoho "Dashboard Maria" reinventada: por dia, cada pessoa tem a sua
// própria barra (lado a lado) cuja altura = horas agendadas; o sub-segmento sólido = horas de
// chamada, o resto (esbatido) = restante do turno. Tooltip mostra agendado/chamadas + notas do
// horário. Componente específico da receção (a StackedBarChart partilhada empilha tudo numa barra).
export const ScheduleCallsBarChart = ({ persons, points, height = 380 }: ScheduleCallsBarChartProps) => {
    const renderTooltip = (tooltipProps: any) => {
        if (!tooltipProps.active || !tooltipProps.payload || tooltipProps.payload.length === 0) return null;
        const point = tooltipProps.payload[0].payload as Record<string, string | number>;
        const personRows = persons
            .map((person) => {
                const personRow = {
                    person,
                    calls: Number(point[`${person.key}__calls`] || 0),
                    scheduled: Number(point[`${person.key}__sched`] || 0),
                    note: String(point[`${person.key}__note`] || ""),
                };
                return personRow;
            })
            .filter((personRow) => personRow.calls > 0 || personRow.scheduled > 0);
        if (personRows.length === 0) return null;
        return (
            <Paper elevation={3} sx={{ p: 1.5, maxWidth: 340 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {String(point.day)}
                </Typography>
                {personRows.map((personRow) => (
                    <Box key={personRow.person.key} sx={{ mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: personRow.person.color }}>
                            {personRow.person.label}
                        </Typography>
                        <Typography variant="caption" display="block">
                            Chamadas {formatHours(personRow.calls)} de {formatHours(personRow.scheduled)} agendadas
                        </Typography>
                        {personRow.note && (
                            <Typography variant="caption" display="block" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                                {personRow.note}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Paper>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={points} margin={{ top: 20, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 12 }} label={{ value: "Horas", angle: -90, position: "insideLeft", fontSize: 12 }} />
                <Tooltip content={renderTooltip} />
                <Legend />
                {persons.flatMap((person) => [
                    <Bar
                        key={`${person.key}__calls`}
                        dataKey={`${person.key}__calls`}
                        name={person.label}
                        stackId={person.key}
                        fill={person.color}
                        isAnimationActive={false}
                    >
                        <LabelList dataKey={`${person.key}__calls`} position="center" fontSize={9} fill="#ffffff" formatter={labelForHours} />
                    </Bar>,
                    <Bar
                        key={`${person.key}__rest`}
                        dataKey={`${person.key}__rest`}
                        stackId={person.key}
                        fill={person.color}
                        fillOpacity={0.28}
                        legendType="none"
                        isAnimationActive={false}
                    >
                        <LabelList dataKey={`${person.key}__sched`} position="top" fontSize={9} formatter={labelForHours} />
                    </Bar>,
                ])}
            </BarChart>
        </ResponsiveContainer>
    );
};
